import {
    BehaviorSubject,
    filter,
    map,
    type Observer,
    Subject,
    withLatestFrom,
} from 'rxjs';
import * as AmazonCognitoIdentity from 'amazon-cognito-identity-js';
import { derived, readable } from 'svelte/store';
import { decodeJwt } from 'jose';
import config from '../../../../aws-config.json';

const tokenKey = 'jwtToken';
const usernameKey = 'username';

function getTokenLifetime(jwt?: string): number {
    if (!jwt) {
        return -1;
    }
    try {
        const claims = decodeJwt(jwt);
        return (claims.exp ?? 0) * 1000 - Date.now();
    } catch (e) {
        return -1;
    }
}

const poolData = {
    UserPoolId: config.PptAiUserPoolStack.userPoolId,
    ClientId: config.PptAiUserPoolStack.userPoolClientId,
};
const userPool = new AmazonCognitoIdentity.CognitoUserPool(poolData);

export enum LoginStatus {
    LOGGED_OUT,
    LOGGED_IN,
    NEW_PASSWORD_REQUIRED,
    PASSWORD_CHANGE_FAILED,
}

export type LoggedIn = {
    status: LoginStatus.LOGGED_IN;
    token: string;
    user: AmazonCognitoIdentity.CognitoUser;
};
export type LoggedOut = { status: LoginStatus.LOGGED_OUT; error?: unknown };
export type NewPasswordRequired = {
    status: LoginStatus.NEW_PASSWORD_REQUIRED;
    user: AmazonCognitoIdentity.CognitoUser;
};
export type PasswordChangeFailed = {
    status: LoginStatus.PASSWORD_CHANGE_FAILED;
    user: AmazonCognitoIdentity.CognitoUser;
    error: unknown;
    userError: string;
};
export type UserLoginStatus =
    | LoggedIn
    | LoggedOut
    | NewPasswordRequired
    | PasswordChangeFailed;

export interface Credentials {
    username: string;
    password: string;
}

export interface NewPassword {
    password: string;
    repeatedPassword: string;
}

function getInitialLoginState(): UserLoginStatus {
    const storedToken = localStorage.getItem(tokenKey);
    const storedUsername = localStorage.getItem(usernameKey);
    if (storedToken && storedUsername) {
        const cognitoUser = new AmazonCognitoIdentity.CognitoUser({
            Username: storedUsername,
            Pool: userPool,
        });

        return {
            status: LoginStatus.LOGGED_IN,
            token: storedToken,
            user: cognitoUser,
        };
    }

    return {
        status: LoginStatus.LOGGED_OUT,
    };
}

const $loginStatus = new BehaviorSubject<UserLoginStatus>(
    getInitialLoginState()
);
$loginStatus
    .pipe(
        filter((status) => status.status === LoginStatus.LOGGED_IN),
        map((status) => (status as LoggedIn).token)
    )
    .subscribe({
        next(token) {
            try {
                const lifetime = getTokenLifetime(token);
                setTimeout(() => {
                    $loginStatus.next({
                        status: LoginStatus.LOGGED_OUT,
                    });
                }, lifetime);
            } catch (e) {
                $loginStatus.next({
                    status: LoginStatus.LOGGED_OUT,
                    error: e,
                });
            }
        },
    });
$loginStatus.subscribe({
    next(status) {
        if (status.status === LoginStatus.LOGGED_IN) {
            localStorage.setItem(tokenKey, status.token);
            localStorage.setItem(usernameKey, status.user.getUsername());
        } else {
            localStorage.removeItem(tokenKey);
            localStorage.removeItem(usernameKey);
        }
    },
});
const $credentials = new Subject<Credentials>();
$credentials.subscribe({
    next({ username, password }) {
        const cognitoUser = new AmazonCognitoIdentity.CognitoUser({
            Username: username,
            Pool: userPool,
        });
        const credentials = new AmazonCognitoIdentity.AuthenticationDetails({
            Username: username,
            Password: password,
        });

        cognitoUser.authenticateUser(credentials, {
            onSuccess(result) {
                $loginStatus.next({
                    status: LoginStatus.LOGGED_IN,
                    token: result.getAccessToken().getJwtToken(),
                    user: cognitoUser,
                });
            },
            onFailure(error) {
                $loginStatus.next({
                    status: LoginStatus.LOGGED_OUT,
                    error,
                });
            },
            newPasswordRequired() {
                $loginStatus.next({
                    status: LoginStatus.NEW_PASSWORD_REQUIRED,
                    user: cognitoUser,
                });
            },
        });
    },
});
const $newPassword = new Subject<NewPassword>();
$newPassword.pipe(withLatestFrom($loginStatus)).subscribe({
    next([newPassword, loginStatus]) {
        if (
            loginStatus.status === LoginStatus.NEW_PASSWORD_REQUIRED ||
            loginStatus.status === LoginStatus.PASSWORD_CHANGE_FAILED
        ) {
            if (newPassword.password !== newPassword.repeatedPassword) {
                $loginStatus.next({
                    status: LoginStatus.PASSWORD_CHANGE_FAILED,
                    user: loginStatus.user,
                    error: 'Passwords must match',
                    userError: 'Passwords must match',
                });
                return;
            }

            loginStatus.user.completeNewPasswordChallenge(
                newPassword.password,
                {},
                {
                    onSuccess(result) {
                        $loginStatus.next({
                            status: LoginStatus.LOGGED_IN,
                            token: result.getAccessToken().getJwtToken(),
                            user: loginStatus.user,
                        });
                    },
                    onFailure(error) {
                        $loginStatus.next({
                            status: LoginStatus.PASSWORD_CHANGE_FAILED,
                            user: loginStatus.user,
                            error,
                            userError: error.message,
                        });
                    },
                }
            );
        }
    },
});
const $requestLogout = new Subject<void>();
$requestLogout.pipe(withLatestFrom($loginStatus)).subscribe({
    next([_, loginStatus]) {
        if (loginStatus.status === LoginStatus.LOGGED_IN) {
            loginStatus.user.signOut(() => {
                $loginStatus.next({
                    status: LoginStatus.LOGGED_OUT,
                });
            });
        }
    },
});
export const setCredentials: Observer<Credentials> = $credentials;
export const setNewPassword: Observer<NewPassword> = $newPassword;
export const loginStatus$ = $loginStatus.asObservable();
export const loginStatus = readable($loginStatus.value, (set) => {
    $loginStatus.subscribe({
        next(status) {
            set(status);
        },
    });
});
export const isLoggedIn = derived(loginStatus, (status) => {
    return status.status === LoginStatus.LOGGED_IN;
});
export const requestLogout: Observer<void> = $requestLogout;
